import { RelatedProductsDocument } from "./../../graphql/Product.mjs";
import { resolveNewInRangeFilterOption } from "./utils.mjs";
import { mapProductListing } from "./mapper.mjs";
import { useSsrCoreApp } from "../ssrControl.mjs";
import { useAuth } from "../auth/index.mjs";
import {
  CrossSellProductsDocument,
  DiscountedProductsDocument,
  ProductsDocument,
  SearchProductsDocument
} from "../../graphql/Product.mjs";
import { extractValue, toNonNullable } from "../../utils/collections.mjs";
import {
  CustomerCartCrossSellProductsDocument,
  GuestCartCrossSellProductsDocument
} from "../../graphql/Cart.mjs";
import { useStoredCart } from "../../composables/cart/storage.mjs";
import { uniqBy } from "lodash-es";
import { useQuery } from "villus";
import { computed, onMounted, ref, watch } from "vue";
export const replaceReducer = (_, curr) => {
  return curr;
};
export const appendReducer = (old, curr) => {
  return uniqBy([...old, ...curr], "id");
};
export function useProductsGeneric(variables, type = "default", opts = {
  fetchOnMount: true,
  newIn: 0
}) {
  const defaults = { fetchOnMount: true, newIn: 0 };
  const { fetchOnMount, newIn } = { ...defaults, ...opts };
  const products = ref([]);
  const totalCount = ref(0);
  const variablesInput = computed(() => {
    const realValues = extractValue(variables);
    if (newIn) {
      return {
        ...realValues,
        filter: {
          ...realValues?.filter,
          ...resolveNewInRangeFilterOption(newIn)
        }
      };
    }
    return realValues;
  });
  const { data, error, isFetching } = useQuery({
    query: resolveProductTypeQuery(type),
    variables: variablesInput,
    fetchOnMount
  });
  watch(data, (value) => {
    const fetchedPage = data.value?.connection?.page_info?.current_page;
    const reducer = fetchedPage === 1 || !fetchedPage ? replaceReducer : appendReducer;
    products.value = reducer(
      products.value,
      toNonNullable(value?.connection?.nodes).map(mapProductListing)
    );
    totalCount.value = value?.connection?.total_count || 0;
  });
  return {
    products,
    totalCount,
    data,
    error,
    isFetching
  };
}
export function useProducts(variables, opts) {
  const products = ref([]);
  const totalCount = ref(0);
  const { execute, data, error, isFetching } = useQuery({
    query: ProductsDocument.toString(),
    variables,
    fetchOnMount: opts?.fetchOnMount ?? true
  });
  watch(data, (value) => {
    const fetchedPage = data.value?.connection?.page_info?.current_page;
    const reducer = fetchedPage === 1 || !fetchedPage ? replaceReducer : appendReducer;
    products.value = reducer(
      products.value,
      toNonNullable(value?.connection?.nodes).map(mapProductListing)
    );
    totalCount.value = value?.connection?.total_count || 0;
  });
  return {
    execute,
    products,
    totalCount,
    data,
    error,
    isFetching
  };
}
export function useDiscountedProducts(variables, opts) {
  const products = ref([]);
  const totalCount = ref(0);
  const { data, error, isFetching } = useQuery({
    query: DiscountedProductsDocument.toString(),
    variables,
    fetchOnMount: opts?.fetchOnMount || true
  });
  watch(data, (value) => {
    const fetchedPage = data.value?.connection?.page_info?.current_page;
    const reducer = fetchedPage === 1 || !fetchedPage ? replaceReducer : appendReducer;
    products.value = reducer(
      products.value,
      toNonNullable(value?.connection?.nodes).map(mapProductListing)
    );
    totalCount.value = value?.connection?.total_count || 0;
  });
  return {
    products,
    totalCount,
    data,
    error,
    isFetching
  };
}
export function useSearchProducts(variables, opts) {
  const products = ref([]);
  const categoriesResult = ref([]);
  const { data, error, isFetching, execute } = useQuery({
    query: SearchProductsDocument.toString(),
    variables,
    fetchOnMount: opts?.fetchOnMount ?? true
  });
  watch(data, (value) => {
    products.value = value?.search?.catalogsearch_fulltext?.items?.map(mapProductListing).slice(0, 5);
    categoriesResult.value = value?.search?.magento_catalog_category?.items;
  });
  return {
    products,
    isFetching,
    error,
    execute,
    categoriesResult
  };
}
export async function useCrossSellProducts(urlKey) {
  const { execute } = useQuery({
    query: CrossSellProductsDocument.toString(),
    variables: {
      slug: urlKey
    },
    fetchOnMount: false
  });
  const response = await execute();
  return response?.data?.product?.items?.filter((item) => item)?.map((item) => mapProductListing(item)) || [];
}
const fetchCustomerCartCrossSellProducts = async () => {
  const { execute } = useQuery({
    query: CustomerCartCrossSellProductsDocument.toString(),
    cachePolicy: "network-only",
    fetchOnMount: false
  });
  const { data } = await execute();
  return data?.cart?.crossSell?.items?.filter((item) => !!item)?.map(mapProductListing);
};
const fetchGuestCartCrossSellProducts = async (cartId) => {
  const { execute } = useQuery({
    query: GuestCartCrossSellProductsDocument.toString(),
    variables: { cartId },
    cachePolicy: "network-only",
    fetchOnMount: false
  });
  const { data } = await execute();
  return data?.cart?.crossSell?.items?.filter((item) => !!item)?.map(mapProductListing);
};
const useCustomerCartCrossSellProducts = async (_fetchCustomerCartCrossSellProducts = fetchCustomerCartCrossSellProducts) => {
  return await _fetchCustomerCartCrossSellProducts();
};
const useGuestCartCrossSellProducts = async (cartId, _fetchGuestCartCrossSellProducts = fetchGuestCartCrossSellProducts) => {
  return await _fetchGuestCartCrossSellProducts(cartId);
};
export function useCartCrossProducts() {
  const { isLoggedIn } = useAuth();
  const { cartState } = useStoredCart();
  const { waitForPageToBeLoaded } = useSsrCoreApp();
  const items = ref([]);
  onMounted(async () => {
    await waitForPageToBeLoaded();
    items.value = await (isLoggedIn.value ? await useCustomerCartCrossSellProducts() : useGuestCartCrossSellProducts(cartState.value?.cartId ?? "")) || [];
  });
  return { items: computed(() => items.value) };
}
export async function useRelatedProducts(urlKey) {
  const { execute } = useQuery({
    query: RelatedProductsDocument.toString(),
    variables: {
      slug: urlKey
    },
    fetchOnMount: false
  });
  const response = await execute();
  return response?.data?.product?.items?.filter((item) => item)?.map((item) => mapProductListing(item)) || [];
}
function resolveProductTypeQuery(type) {
  if (type === "discounted") {
    return DiscountedProductsDocument.toString();
  }
  if (type === "default") {
    return ProductsDocument.toString();
  }
  return ProductsDocument.toString();
}
